import { supabase } from './supabase';

export interface PortalSessionResult {
  url: string;
  customer_id: string;
  expires_at: string;
}

export interface PortalConfiguration {
  id: string;
  is_default: boolean;
  active: boolean;
  features: {
    customer_update?: {
      enabled: boolean;
      allowed_updates: string[];
    };
    invoice_history?: {
      enabled: boolean;
    };
    payment_method_update?: {
      enabled: boolean;
    };
    subscription_cancel?: {
      enabled: boolean;
      mode: string;
      proration_behavior: string;
    };
    subscription_update?: {
      enabled: boolean;
      default_allowed_updates: string[];
      proration_behavior: string;
    };
  };
}

export class CustomerPortalService {
  /**
   * Create a customer portal session for a user
   * This redirects the user to Stripe's customer portal
   */
  static async createPortalSession(userId: string, returnUrl?: string): Promise<PortalSessionResult> {
    try {
      // Use current domain as default return URL
      const defaultReturnUrl = typeof window !== 'undefined'
        ? `${window.location.origin}/dashboard/settings?tab=billing&portal=success`
        : 'https://rulevia.com/dashboard/settings?tab=billing';

      const finalReturnUrl = returnUrl || defaultReturnUrl;

      const { data, error } = await supabase.functions.invoke('stripe-customer-portal', {
        body: {
          action: 'get_customer_portal_url',
          params: {
            user_id: userId,
            return_url: finalReturnUrl
          }
        }
      });

      if (error) {
        console.error('❌ Portal session creation failed:', error);
        throw new Error(`Failed to create portal session: ${error.message}`);
      }

      if (!data?.url) {
        throw new Error('No portal URL returned from Stripe');
      }

      return data as PortalSessionResult;

    } catch (error) {
      console.error('Error creating portal session:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to create customer portal session'
      );
    }
  }

  /**
   * Redirect user to customer portal
   * Opens portal in same window by default, or new tab if specified
   */
  static async redirectToPortal(
    userId: string, 
    returnUrl?: string, 
    openInNewTab: boolean = false
  ): Promise<void> {
    try {
      const portalSession = await this.createPortalSession(userId, returnUrl);
      

      if (openInNewTab) {
        // Open in new tab
        const newWindow = window.open(portalSession.url, '_blank');
        if (!newWindow) {
          throw new Error('Popup blocked. Please allow popups and try again.');
        }
      } else {
        // Redirect in current window
        window.location.href = portalSession.url;
      }

    } catch (error) {
      console.error('Error redirecting to portal:', error);
      throw error;
    }
  }


  /**
   * Check if user has access to customer portal
   * Returns true if user has a subscription with Stripe customer ID (active or canceled but still in period)
   */
  static async hasPortalAccess(userId: string): Promise<boolean> {
    try {
      const { data: subscription, error } = await supabase
        .from('user_subscriptions')
        .select('stripe_customer_id, status, current_period_end')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !subscription) {
        return false;
      }

      // User has portal access if they have a Stripe customer ID
      // This includes canceled subscriptions that are still within their billing period
      const hasAccess = !!subscription.stripe_customer_id;
      

      return hasAccess;

    } catch (error) {
      console.error('Error checking portal access:', error);
      return false;
    }
  }

  /**
   * Get user's subscription status for portal context
   */
  static async getSubscriptionContext(userId: string): Promise<{
    hasAccess: boolean;
    planName: string | null;
    status: string | null;
    nextBillingDate: string | null;
    cancelAtPeriodEnd: boolean;
  }> {
    try {
      // Check if user is a team member and get organization owner if so
      const { data: userData } = await supabase
        .from('users')
        .select('organization_id, team_role')
        .eq('id', userId)
        .single();

      let targetUserId = userId;
      if (userData?.organization_id && userData?.team_role) {
        // Check if this user is a team member (not organization owner)
        const { data: orgData } = await supabase
          .from('organizations')
          .select('created_by')
          .eq('id', userData.organization_id)
          .single();
        
        if (orgData?.created_by && orgData.created_by !== userId) {
          // This is a team member - use organization owner's subscription
          targetUserId = orgData.created_by;
        }
      }

      // Get the subscription for the target user (original user or organization owner) using RPC
      const { data: subscriptions, error } = await supabase
        .rpc('get_team_aware_subscription_context', { target_user_id: targetUserId });
      
      const subscription = subscriptions && subscriptions.length > 0 ? subscriptions[0] : null;

      if (error || !subscription) {
        return {
          hasAccess: false,
          planName: null,
          status: null,
          nextBillingDate: null,
          cancelAtPeriodEnd: false
        };
      }

      // Then get the plan details
      const { data: plan } = await supabase
        .from('subscription_plans')
        .select('name')
        .eq('id', subscription.plan_id)
        .single();

      // Check if subscription is still within billing period (even if canceled)
      const now = new Date();
      const isWithinPeriod = subscription.current_period_end 
        ? new Date(subscription.current_period_end) > now 
        : false;
      
      // For canceled subscriptions, show appropriate status
      let effectiveStatus = subscription.status;
      if (subscription.status === 'canceled' && isWithinPeriod) {
        effectiveStatus = subscription.cancel_at_period_end ? 'active (canceling)' : 'canceled (active until period end)';
      } else if (subscription.status === 'canceled' && !isWithinPeriod) {
        // Subscription is truly expired
        return {
          hasAccess: false,
          planName: null,
          status: null,
          nextBillingDate: null,
          cancelAtPeriodEnd: false
        };
      }

      return {
        hasAccess: !!subscription.stripe_customer_id,
        planName: plan?.name || subscription.plan_id, // Fallback to plan_id if name not found
        status: effectiveStatus,
        nextBillingDate: subscription.current_period_end,
        cancelAtPeriodEnd: subscription.cancel_at_period_end || false
      };

    } catch (error) {
      console.error('Error getting subscription context:', error);
      return {
        hasAccess: false,
        planName: null,
        status: null,
        nextBillingDate: null,
        cancelAtPeriodEnd: false
      };
    }
  }

  /**
   * Handle return from customer portal
   * Call this when user returns from portal to update local state
   */
  static async handlePortalReturn(userId: string): Promise<{
    success: boolean;
    message: string;
    subscriptionUpdated: boolean;
  }> {
    try {

      // Refresh user subscription data
      // The webhook should have already updated the database,
      // but we'll verify the current state
      const context = await this.getSubscriptionContext(userId);
      

      // You can add logic here to show specific messages based on
      // what might have changed in the portal
      let message = 'Your subscription has been updated successfully.';
      
      if (context.cancelAtPeriodEnd) {
        message = 'Your subscription cancellation has been processed. It will remain active until your next billing date.';
      } else if (context.status === 'active') {
        message = 'Your subscription is active and up to date.';
      }

      return {
        success: true,
        message,
        subscriptionUpdated: true
      };

    } catch (error) {
      console.error('Error handling portal return:', error);
      return {
        success: false,
        message: 'There was an issue updating your subscription. Please contact support if problems persist.',
        subscriptionUpdated: false
      };
    }
  }
}